<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import { useMainStore } from "@/stores/main";
import FormControlIcon from "@/Components/FormControlIcon.vue";
import BaseIcon from "@/Components/BaseIcon.vue";
import { mdiInformationOutline } from "@mdi/js";

const props = defineProps({
    name: {
        type: String,
        default: null,
    },
    id: {
        type: String,
        default: null,
    },
    autocomplete: {
        type: String,
        default: null,
    },
    placeholder: {
        type: String,
        default: null,
    },
    inputmode: {
        type: String,
        default: null,
    },
    icon: {
        type: String,
        default: null,
    },
    options: {
        type: Array,
        default: null,
    },
    type: {
        type: String,
        default: "text",
    },
    tooltipcontenthtml: {
        type: String,
        default: null,
    },
    tooltipcontent: {
        type: String,
        default: null,
    },

    modelValue: {
        type: [String, Number, Boolean, Array, Object],
        default: "",
    },
    required: Boolean,
    borderless: Boolean,
    transparent: Boolean,
    ctrlKFocus: Boolean,
    readonly: Boolean,
    // LRR
    labelinput: {
        type: String,
        default: null,
    },
    canBeNull: Boolean,
});

const emit = defineEmits(["update:modelValue", "setRef"]);

const computedValue = computed({
    get: () => props.modelValue,
    set: (value) => {
        emit("update:modelValue", value);
    },
});

const inputElClass = computed(() => {
    const base = [
        "px-3 py-2 max-w-full focus:ring focus:outline-none border-gray-700 rounded w-full",
        "dark:placeholder-gray-400",
        // computedType.value === "textarea" ? "h-24" : "h-12",
        props.borderless ? "border-0" : "border",
        props.transparent ? "bg-transparent" : "bg-white dark:bg-slate-800",
    ];

    if (props.icon) {
        base.push("pl-10");
    }

    return base;
});

const computedType = computed(() => (props.options ? "select" : props.type));

const controlIconH = computed(() =>
    // props.type === "textarea" ? "h-full" : "h-12"
    props.type === "textarea" ? "h-[38px]" : "h-[38px]"
);

const mainStore = useMainStore();

const selectEl = ref(null);

const textareaEl = ref(null);

const inputEl = ref(null);

onMounted(() => {
    if (selectEl.value) {
        emit("setRef", selectEl.value);
    } else if (textareaEl.value) {
        emit("setRef", textareaEl.value);
    } else {
        emit("setRef", inputEl.value);
    }
});

if (props.ctrlKFocus) {
    const fieldFocusHook = (e) => {
        if (e.ctrlKey && e.key === "k") {
            e.preventDefault();
            inputEl.value.focus();
        } else if (e.key === "Escape") {
            inputEl.value.blur();
        }
    };

    onMounted(() => {
        if (!mainStore.isFieldFocusRegistered) {
            window.addEventListener("keydown", fieldFocusHook);
            mainStore.isFieldFocusRegistered = true;
        } else {
            // console.error('Duplicate field focus event')
        }
    });

    onBeforeUnmount(() => {
        window.removeEventListener("keydown", fieldFocusHook);
        mainStore.isFieldFocusRegistered = false;
    });
}
</script>

<template>
    <div class="relative">
        <div v-if="labelinput" class="text-xs font-bold text-gray-500 dark:text-slate-400 mt-1">
            {{ labelinput }}
            <span v-if="tooltipcontent || tooltipcontenthtml">
                <a href="#" class="group relative inline-block text-blue-500 underline hover:text-red-500 duration-300">
                    <BaseIcon :path="mdiInformationOutline" size="15" />
                    <!-- quitar text-center -->
                    <div v-if="tooltipcontenthtml" v-html="tooltipcontenthtml" class="font-light absolute hidden group-hover:flex -left-5 -top-2 -translate-y-full w-96 px-2 py-1 bg-gray-700 rounded-lg text-white text-sm after:content-[''] after:absolute after:left-1/2 after:top-[100%] after:-translate-x-1/2 after:border-8 after:border-x-transparent after:border-b-transparent after:border-t-gray-700">
                    </div>
                    <span v-if="tooltipcontent" class="font-light absolute hidden group-hover:flex -left-5 -top-2 -translate-y-full w-64 px-2 py-1 bg-gray-700 rounded-lg text-center text-white text-sm after:content-[''] after:absolute after:left-1/2 after:top-[100%] after:-translate-x-1/2 after:border-8 after:border-x-transparent after:border-b-transparent after:border-t-gray-700">
                        {{ tooltipcontent }}
                    </span>
                </a>
            </span>
        </div>

        <select v-if="computedType === 'select'" :id="id" v-model="computedValue" :name="name" :class="inputElClass">
            <!-- LRR change :value="option"  to :value="option.id ?? option" AND add option selected, null para ambos casos de crear o editar muestr el placeholder -->
            <option class="text-xs text-gray-500 dark:text-slate-400 mt-1" v-if="placeholder" value="null" :disabled="!canBeNull" selected :hidden="!canBeNull">{{ placeholder }}</option>
            <option v-for="option in options" :key="option.id ?? option" :value="option.id ?? option">
                {{ option.label ?? option }}
            </option>
        </select>
        <textarea v-else-if="computedType === 'textarea'" :id="id" v-model="computedValue" :class="inputElClass" :name="name" :placeholder="placeholder" :required="required" :readonly="readonly" />
        <!-- LRR select yesno -->
        <select v-else-if="computedType === 'yesno'" :id="id" v-model="computedValue" :name="name" :class="inputElClass" :disabled="readonly">
            <option value="0">No {{ placeholder }}</option>
            <option value="1">Si {{ placeholder }}</option>
        </select>
        <input v-else :id="id" ref="inputEl" v-model="computedValue" :name="name" :inputmode="inputmode" :autocomplete="autocomplete" :required="required" :placeholder="placeholder" :type="computedType" :class="inputElClass" :readonly="readonly" />
        <FormControlIcon v-if="icon" :icon="icon" :h="controlIconH" />
        <!-- <input v-if="computedType === 'yesno' && readonly" type="hidden" value=""> -->
    </div>
</template>
